import {
  CustomerOrderDetailsDto,
  OrdersApi,
  PaymeraRetryPaymentResponseDto,
} from '@/app/services/orders.api';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
  OnInit,
} from '@angular/core';
import { RouterLink, ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import {
  ShamDeliveryTruck1Icon,
  ShamLocationAddressIcon,
  ShamArrowLineLeftIcon,
  ShamPaymentSuccess01Icon,
} from 'sham-components/icons';

import { MEDIA_BASE_URL } from '@/environments/environment';
import { RefundsApi } from '@/app/services/refunds.api';
import {
  CustomerRefusalFinancialOutcome,
  CustomerRefusalOutcome,
} from 'sham-components/shared/customer-refusal-outcome';

@Component({
  selector: 'app-order-details-page',
  imports: [
    RouterLink,
    CommonModule,
    ShamDeliveryTruck1Icon,
    ShamLocationAddressIcon,
    ShamArrowLineLeftIcon,
    TranslateModule,
    ShamPaymentSuccess01Icon,
    CustomerRefusalOutcome,
  ],
  templateUrl: './order-details-page.html',
  styleUrl: './order-details-page.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrderDetailsPage implements OnInit {
  private router = inject(Router);

  orderId = signal<string>('');
  private route = inject(ActivatedRoute);
  private readonly ordersApi = inject(OrdersApi);
  private readonly refundsApi = inject(RefundsApi);

  orderDetails = signal<CustomerOrderDetailsDto | null>(null);
  isLoading = signal<boolean>(true);
  isRetryingPayment = signal(false);
  readonly refusalOutcomes = signal<
    Record<string, CustomerRefusalFinancialOutcome | null>
  >({});
  private readonly refusalOutcomeRequestIds = new Map<string, number>();
  canShowRetryPayment = computed(
    () => !!this.orderDetails()?.canRetry && !this.isRetryingPayment(),
  );

  ngOnInit() {
    const idFromRoute = this.route.snapshot.paramMap.get('id');
    if (idFromRoute) {
      this.orderId.set(idFromRoute);
      this.loadOrderDetails(idFromRoute);
    }
  }

  loadOrderDetails(id: string) {
    this.isLoading.set(true);
    this.ordersApi.getOrderDetails(id).subscribe({
      next: (data) => {
        this.orderDetails.set(data);
        this.refusalOutcomes.set({});
        // Customer-refusal outcome UI is pending an approved Figma design.
        // for (const sellerOrder of data.sellerOrders) {
        //   this.loadCustomerRefusalOutcome(sellerOrder.sellerOrderId);
        // }
        this.isLoading.set(false);
      },
      error: (err) => {
        console.error('Error fetching order details:', err);
        this.isLoading.set(false);
      },
    });
  }

  loadCustomerRefusalOutcome(sellerOrderId: string) {
    const requestId = (this.refusalOutcomeRequestIds.get(sellerOrderId) ?? 0) + 1;
    this.refusalOutcomeRequestIds.set(sellerOrderId, requestId);
    this.refundsApi.getCustomerRefusalOutcome(sellerOrderId).subscribe({
      next: (outcome) => {
        if (this.refusalOutcomeRequestIds.get(sellerOrderId) !== requestId) return;
        this.refusalOutcomes.update((current) => ({
          ...current,
          [sellerOrderId]: outcome,
        }));
      },
      error: (error: unknown) => {
        if (this.refusalOutcomeRequestIds.get(sellerOrderId) !== requestId) return;
        this.refusalOutcomes.update((current) => ({
          ...current,
          [sellerOrderId]: null,
        }));
        if (!(error instanceof HttpErrorResponse) || error.status !== 404) {
          console.error('Error fetching customer-refusal outcome:', error);
        }
      },
    });
  }

  goBack() {
    this.router.navigate(['/home']);
  }

  getOptionsText(options: any[]): string {
    if (!options || options.length === 0) return '';

    return options
      .map((opt) => `${opt.attributeName}: ${opt.attributeValueName}`)
      .join(', ');
  }

  goToSellerProfile(sellerId: string) {
    this.router.navigate(['/seller', sellerId]);
  }

  retryPayment() {
    const orderId = this.orderDetails()?.orderId;
    if (!orderId || !this.orderDetails()?.canRetry || this.isRetryingPayment()) {
      return;
    }

    this.isRetryingPayment.set(true);
    this.ordersApi.retryPaymeraPayment(orderId).subscribe({
      next: (response: PaymeraRetryPaymentResponseDto) => {
        this.isRetryingPayment.set(false);
        window.location.href = response.hostedPaymentUrl;
      },
      error: (err) => {
        console.error('Error retrying payment:', err);
        this.isRetryingPayment.set(false);
      },
    });
  }

  getImageUrl(path?: string | null): string {
    if (!path) return '';
    return path.startsWith('http') ? path : `${MEDIA_BASE_URL}${path}`;
  }
  goToBack() {
    history.back();
  }
}
